/**
 * Pure helpers for the "Fais passer le mot" share page (/share).
 *
 * Customer.io emails can't run JavaScript and some clients strip `sms:` links,
 * so the email buttons point to /share?via=<channel> and this module builds
 * everything the page needs to finish the share in the browser.
 */

export type ShareChannel =
    | "sms"
    | "whatsapp"
    | "email"
    | "copy"
    | "direct_link";

export type UtmMedium = "sms" | "whatsapp" | "email" | "direct_link";

export type MobilePlatform = "ios" | "android";

export type ShareOutcome = "shared" | "cancelled" | "copied" | "manual";

export const DEFAULT_SHARE_CHANNEL: ShareChannel = "direct_link";

const SHARE_CHANNELS: ShareChannel[] = [
    "sms",
    "whatsapp",
    "email",
    "copy",
    "direct_link",
];

const SHARE_BASE_URL = "https://prelib.com/";

// Shared content stays in French regardless of UI language, to match the email.
export const SHARE_TITLE = "Prelib";

export const SHARE_EMAIL_SUBJECT =
    "Je pensais que Prelib pourrait t’intéresser 💜";

const SHARE_MESSAGE_INTRO =
    "Allô! Je suis tombé·e sur Prelib, un service québécois qui rend le dépistage des ITSS vraiment plus simple. Je me suis dit que ça pourrait t’intéresser. 😊";

export const parseShareChannel = (
    value: string | null | undefined
): ShareChannel => {
    const normalized = (value ?? "").trim().toLowerCase();
    return SHARE_CHANNELS.find((channel) => channel === normalized)
        ? (normalized as ShareChannel)
        : DEFAULT_SHARE_CHANNEL;
};

/** "Copy link" is tracked as direct_link, matching the email's UTMs. */
export const getUtmMedium = (channel: ShareChannel): UtmMedium =>
    channel === "copy" ? "direct_link" : channel;

export const buildShareUrl = (channel: ShareChannel): string => {
    const params = new URLSearchParams({
        utm_source: "patient_share",
        utm_medium: getUtmMedium(channel),
        utm_campaign: "fais_passer_le_mot",
        utm_content: "customerio_share_block",
    });
    return `${SHARE_BASE_URL}?${params.toString()}`;
};

export const buildShareMessage = (shareUrl: string): string =>
    `${SHARE_MESSAGE_INTRO}\n\n${shareUrl}`;

/**
 * Payload for the native share sheet.
 * The link belongs only in `url`. Share targets append `url` to `text`, so
 * a message from `buildShareMessage` (which already ends with the URL) would
 * show the link twice. Clipboard fallback still copies `url`.
 */
export const buildShareSheetData = (
    shareUrl: string
): { title: string; text: string; url: string } => ({
    title: SHARE_TITLE,
    text: SHARE_MESSAGE_INTRO,
    url: shareUrl,
});

/**
 * Returns the mobile platform, or null on desktop.
 * iPadOS 13+ reports a Macintosh user agent, so touch support disambiguates it.
 */
export const detectMobilePlatform = (
    userAgent: string,
    maxTouchPoints = 0
): MobilePlatform | null => {
    if (/android/i.test(userAgent)) return "android";
    if (/iphone|ipad|ipod/i.test(userAgent)) return "ios";
    if (/macintosh/i.test(userAgent) && maxTouchPoints > 1) return "ios";
    return null;
};

/** iOS expects `sms:&body=`, Android expects `sms:?body=`. */
export const buildSmsUrl = (
    platform: MobilePlatform,
    message: string
): string => {
    const separator = platform === "ios" ? "&" : "?";
    return `sms:${separator}body=${encodeURIComponent(message)}`;
};

export const buildWhatsAppUrl = (message: string): string =>
    `https://wa.me/?text=${encodeURIComponent(message)}`;

export const buildMailtoUrl = (subject: string, message: string): string =>
    `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
        message
    )}`;

type ShareNavigator = {
    share?: (data: ShareData) => Promise<void>;
    clipboard?: Pick<Clipboard, "writeText">;
};

const isAbortError = (error: unknown): boolean =>
    (error as { name?: string } | null)?.name === "AbortError";

/**
 * Copies the URL to the clipboard.
 * Resolves "manual" when the clipboard is unavailable, so the UI can select the URL instead.
 */
export const copyLink = async (
    url: string,
    nav: ShareNavigator
): Promise<ShareOutcome> => {
    if (!nav.clipboard?.writeText) return "manual";
    try {
        await nav.clipboard.writeText(url);
        return "copied";
    } catch (error) {
        return "manual";
    }
};

/**
 * Opens the native share sheet when available, otherwise copies the URL.
 * A user cancelling the share sheet resolves "cancelled" and nothing else happens.
 */
export const shareOrCopyLink = async (
    data: { title: string; text: string; url: string },
    nav: ShareNavigator
): Promise<ShareOutcome> => {
    if (nav.share) {
        try {
            await nav.share(data);
            return "shared";
        } catch (error) {
            if (isAbortError(error)) return "cancelled";
            // Share not permitted (e.g. no user gesture): fall back to copy.
        }
    }
    return copyLink(data.url, nav);
};
